import React, { useEffect, useState } from 'react'
import { FormCustom, Input, SwitchToggle } from '../../../components/form'
import ReactPlaceholder from 'react-placeholder/lib'
import { useGetId } from '../../../hooks/process-data'
import { doToast } from '../../../utils'
import { toast } from 'react-toastify'
import { doRequestGet, doRequestFile } from '../../../api'
import { Button } from 'react-bootstrap'

const ProductForm = ({ rowId, setReload }) => {
  const [row, setRow] = useState({
    p_id_product: '',
    p_name_product: '',
    p_id_type_product: '',
    p_enabled_product: 1,
    p_image_product: null,
  })
  const [skus, setSkus] = useState([])
  const [types, setTypes] = useState([])
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)

  const { isProcessing, getRow, error } = useGetId({
    url: '/product/' + rowId,
    rowId: rowId || null
  })

  useEffect(() => {
    const load = async () => {
      if (error) {
        setReload?.()
        return
      }
      if (!isProcessing && !ready) {
        const res = await doRequestGet({ url: '/product/type' })
        if (res && res.data) setTypes(res.data)
        if (rowId && getRow && getRow.id_product) {
          setRow({
            p_id_product: getRow.id_product,
            p_name_product: getRow.name_product || '',
            p_id_type_product: getRow.id_type_product || '',
            p_enabled_product: getRow.enabled_product ?? 1,
            p_image_product: null,
          })
          setSkus((getRow.skus || []).map(s => ({
            id_product_sku: s.id_product_sku,
            size_product_sku: s.size_product_sku || '',
            color_product_sku: s.color_product_sku || '',
            price_product_sku: s.price_product_sku ?? '',
            stock_product_sku: s.stock_product_sku ?? 0,
          })))
        } else if (!rowId) {
          setSkus([{ id_product_sku: '', size_product_sku: '', color_product_sku: '', price_product_sku: '', stock_product_sku: 0 }])
        }
        setReady(true)
      }
    }
    load()
  }, [isProcessing, getRow, error, rowId, ready, setReload])

  const addSku = () => {
    setSkus(s => [...s, { id_product_sku: '', size_product_sku: '', color_product_sku: '', price_product_sku: '', stock_product_sku: 0 }])
  }

  const removeSku = (index) => {
    setSkus(s => s.filter((_, i) => i !== index))
  }

  const updateSku = (index, field, value) => {
    setSkus(s => s.map((item, i) => i === index ? { ...item, [field]: value } : item))
  }

  const handleSubmit = async ({ e }) => {
    e.preventDefault()
    if (!row.p_name_product || !row.p_id_type_product) {
      doToast({ response: 'warning', message: 'Complete nombre y tipo de producto.', toast })
      return
    }
    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('p_id_product', row.p_id_product || '')
      formData.append('p_name_product', row.p_name_product)
      formData.append('p_id_type_product', row.p_id_type_product)
      formData.append('p_enabled_product', row.p_enabled_product)
      formData.append('p_skus', JSON.stringify(skus.filter(s => s.size_product_sku && s.color_product_sku)))
      if (row.p_image_product && row.p_image_product instanceof File) {
        formData.append('p_image_product', row.p_image_product)
      }
      const res = await doRequestFile({ url: '/product', data: formData })
      doToast({ ...res, toast })
      if (res.response === 'success') {
        setReload?.(true)
        if (typeof setReload === 'function') setReload(true)
      }
    } catch (err) {
      doToast({ response: 'danger', message: 'Error al guardar.', toast })
    } finally {
      setSaving(false)
    }
  }

  return (
    <ReactPlaceholder ready={ready} rows={5}>
      <FormCustom onSubmit={handleSubmit} loadSubmit={saving}>
        <Input
          text="Nombre"
          placeholder="Nombre del producto"
          classNameParent="col-12 mb-3"
          required
          value={row.p_name_product}
          onChange={e => setRow({ ...row, p_name_product: e.target.value })}
        />
        <div className="col-12 mb-3">
          <label className="form-label">Tipo</label>
          <select
            className="form-select"
            value={row.p_id_type_product}
            onChange={e => setRow({ ...row, p_id_type_product: e.target.value })}
            required
          >
            <option value="">Seleccione tipo</option>
            {(types || []).map(t => (
              <option key={t.id_type_product} value={t.id_type_product}>{t.name_type_product}</option>
            ))}
          </select>
        </div>
        <div className="col-12 mb-3">
          <label className="form-label">Imagen (opcional)</label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            onChange={e => setRow({ ...row, p_image_product: e.target.files[0] || null })}
          />
        </div>
        <SwitchToggle
          id="product-enabled"
          textCheck="Desactivar"
          textUnChecked="Activar"
          classNameParent="col-12 mb-3"
          checked={row.p_enabled_product === 1}
          onChange={e => setRow({ ...row, p_enabled_product: e.target.checked ? 1 : 0 })}
        />
        <div className="col-12 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">Variantes (talla, color, precio, stock)</label>
            <Button type="button" variant="outline-primary" size="sm" onClick={addSku}>Agregar</Button>
          </div>
          {skus.map((sku, index) => (
            <div key={index} className="row g-2 mb-2 align-items-end">
              <div className="col-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Talla"
                  value={sku.size_product_sku}
                  onChange={e => updateSku(index, 'size_product_sku', e.target.value)}
                />
              </div>
              <div className="col-2">
                <input
                  className="form-control form-control-sm"
                  placeholder="Color"
                  value={sku.color_product_sku}
                  onChange={e => updateSku(index, 'color_product_sku', e.target.value)}
                />
              </div>
              <div className="col-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control form-control-sm"
                  placeholder="Precio"
                  value={sku.price_product_sku}
                  onChange={e => updateSku(index, 'price_product_sku', e.target.value)}
                />
              </div>
              <div className="col-2">
                <input
                  type="number"
                  min="0"
                  className="form-control form-control-sm"
                  placeholder="Stock"
                  value={sku.stock_product_sku}
                  onChange={e => updateSku(index, 'stock_product_sku', parseInt(e.target.value, 10) || 0)}
                />
              </div>
              <div className="col-auto">
                <Button type="button" variant="outline-danger" size="sm" onClick={() => removeSku(index)}>Quitar</Button>
              </div>
            </div>
          ))}
        </div>
      </FormCustom>
    </ReactPlaceholder>
  )
}

ProductForm.defaultProps = {
  rowId: '',
  setReload: () => {},
}

export { ProductForm }
